import React, { useState } from 'react';
import { ESSButton } from '../Atoms/ESSButton';
import { ESSDialog } from '../Molecules/Dialog';

/**
 * DialogExample - Functional example of Dialog component
 * Shows modal dialogs with different sizes
 */
const DialogExample = () => {
  const [openDialog, setOpenDialog] = useState(null);

  const dialogs = [
    {
      id: 'sm',
      size: 'sm',
      label: 'Small Dialog',
      title: 'Small Dialog Example',
    },
    {
      id: 'md',
      size: 'md',
      label: 'Medium Dialog',
      title: 'Medium Dialog Example',
    },
    {
      id: 'lg',
      size: 'lg',
      label: 'Large Dialog',
      title: 'Large Dialog Example',
    },
    {
      id: 'xl',
      size: 'xl',
      label: 'Extra Large Dialog',
      title: 'Extra Large Dialog Example',
    },
  ];

  return (
    <div className="dialog-example">
      <div className="dialog-example__buttons">
        {dialogs.map((dialog) => (
          <ESSButton
            key={dialog.id}
            size="sm"
            onClick={() => setOpenDialog(dialog.id)}
          >
            Open {dialog.label}
          </ESSButton>
        ))}
      </div>

      {/* Render active dialog */}
      {openDialog && (
        <>
          <div
            className="dialog-example__overlay"
            onClick={() => setOpenDialog(null)}
          />
          <div className="dialog-example__container">
            <ESSDialog
              size={dialogs.find((d) => d.id === openDialog)?.size}
              className="dialog-example__dialog"
            >
              <div className="dialog-example__header">
                <h2>{dialogs.find((d) => d.id === openDialog)?.title}</h2>
                <button
                  onClick={() => setOpenDialog(null)}
                  className="dialog-example__close"
                  aria-label="Close dialog"
                >
                  ×
                </button>
              </div>
              <div className="dialog-example__body">
                <p>
                  This is a{' '}
                  {dialogs.find((d) => d.id === openDialog)?.size.toUpperCase()}{' '}
                  sized dialog.
                </p>
                <p>Dialog components are perfect for:</p>
                <ul>
                  <li>Confirmation messages</li>
                  <li>Forms and data entry</li>
                  <li>Detailed information display</li>
                  <li>User interactions that require focus</li>
                </ul>
              </div>
              <div className="dialog-example__footer">
                <ESSButton
                  size="sm"
                  variant="negative"
                  onClick={() => setOpenDialog(null)}
                >
                  Cancel
                </ESSButton>
                <ESSButton
                  size="sm"
                  variant="positive"
                  onClick={() => setOpenDialog(null)}
                >
                  Confirm
                </ESSButton>
              </div>
            </ESSDialog>
          </div>
        </>
      )}

      <style jsx>{`
        .dialog-example__buttons {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .dialog-example__overlay {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 9998;
          animation: fadeIn 0.2s ease-out;
        }

        .dialog-example__container {
          position: fixed;
          inset: 0;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          animation: fadeIn 0.3s ease-out;
        }

        .dialog-example__dialog {
          animation: slideUp 0.3s ease-out;
        }

        .dialog-example__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid var(--ess-color-border, #e5e7eb);
        }

        .dialog-example__header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--ess-color-text-primary, #1f2937);
        }

        .dialog-example__close {
          background: transparent;
          border: none;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
          color: var(--ess-color-text-secondary, #6b7280);
          padding: 0.25rem;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.25rem;
        }

        .dialog-example__close:hover {
          background-color: var(--ess-color-gray-100, #f3f4f6);
          color: var(--ess-color-text-primary, #1f2937);
        }

        .dialog-example__body {
          padding: 1.5rem;
          color: var(--ess-color-text-primary, #1f2937);
        }

        .dialog-example__body ul {
          margin: 1rem 0;
          padding-left: 1.5rem;
        }

        .dialog-example__body li {
          margin: 0.5rem 0;
        }

        .dialog-example__footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          border-top: 1px solid var(--ess-color-border, #e5e7eb);
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(16px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default DialogExample;
